/**
 * FavouritesContext — Wishlist / favourite lists state management
 *
 * Features:
 *   - Create named lists, add/remove products
 *   - Delete lists, rename lists
 *   - Share lists with users in the same customer account
 *   - "New" badge for newly created or received lists
 *   - Persisted to localStorage
 *
 * @module contexts/FavouritesContext
 * @since 2026-05-25
 */
import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FavouritesContext = createContext();

const STORAGE_KEY = 'mrt_favourites';

function generateId() {
  return `fav-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { lists: [] };
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

// Default list seeded on first use
function getDefaultList(userName) {
  return {
    id: 'fav-default',
    name: 'Favourites',
    createdBy: userName || 'Unknown',
    createdDate: '2023-10-10T00:00:00Z',
    isNew: false,
    sharedWith: [],
    items: [],
  };
}

const ACTION = {
  INIT: 'INIT',
  CREATE_LIST: 'CREATE_LIST',
  DELETE_LIST: 'DELETE_LIST',
  RENAME_LIST: 'RENAME_LIST',
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  SHARE_LIST: 'SHARE_LIST',
  MARK_READ: 'MARK_READ',
  ADD_ALL_TO_CART: 'ADD_ALL_TO_CART',
};

function reducer(state, action) {
  switch (action.type) {
    case ACTION.INIT:
      return action.payload;

    case ACTION.CREATE_LIST: {
      const newList = {
        id: generateId(),
        name: action.payload.name,
        createdBy: action.payload.createdBy || 'Unknown',
        createdDate: new Date().toISOString(),
        isNew: true,
        sharedWith: [],
        items: [],
      };
      return { ...state, lists: [newList, ...state.lists] };
    }

    case ACTION.DELETE_LIST:
      return { ...state, lists: state.lists.filter(l => l.id !== action.payload.listId) };

    case ACTION.RENAME_LIST:
      return {
        ...state,
        lists: state.lists.map(l =>
          l.id === action.payload.listId
            ? { ...l, name: action.payload.name }
            : l
        ),
      };

    case ACTION.ADD_ITEM: {
      const { listId, item } = action.payload;
      return {
        ...state,
        lists: state.lists.map(l => {
          if (l.id !== listId) return l;
          // Prevent duplicates by itemCode
          if (l.items.some(i => i.itemCode === item.itemCode)) return l;
          return { ...l, items: [...l.items, { ...item, addedDate: new Date().toISOString() }] };
        }),
      };
    }

    case ACTION.REMOVE_ITEM:
      return {
        ...state,
        lists: state.lists.map(l =>
          l.id === action.payload.listId
            ? { ...l, items: l.items.filter(i => i.itemCode !== action.payload.itemCode) }
            : l
        ),
      };

    case ACTION.SHARE_LIST: {
      const { listId, sharedWithUser } = action.payload;
      return {
        ...state,
        lists: state.lists.map(l => {
          if (l.id !== listId) return l;
          if (l.sharedWith.some(u => u.id === sharedWithUser.id)) return l;
          return { ...l, sharedWith: [...l.sharedWith, sharedWithUser] };
        }),
      };
    }

    case ACTION.MARK_READ:
      return {
        ...state,
        lists: state.lists.map(l =>
          l.id === action.payload.listId ? { ...l, isNew: false } : l
        ),
      };

    default:
      return state;
  }
}

export function FavouritesProvider({ children }) {
  const { account } = useAuth();
  const [state, dispatch] = useReducer(reducer, { lists: [] });

  // Load from localStorage on mount, seed default list if empty
  useEffect(() => {
    const saved = loadFromStorage();
    if (saved.lists.length === 0) {
      saved.lists = [getDefaultList(account?.name || 'Unknown')];
    }
    dispatch({ type: ACTION.INIT, payload: saved });
  }, [account?.name]);

  // Persist to localStorage on every change
  useEffect(() => {
    if (state.lists.length > 0) {
      saveToStorage(state);
    }
  }, [state]);

  const createList = useCallback((name) => {
    dispatch({
      type: ACTION.CREATE_LIST,
      payload: { name, createdBy: account?.name || 'Unknown' },
    });
  }, [account?.name]);

  const deleteList = useCallback((listId) => {
    dispatch({ type: ACTION.DELETE_LIST, payload: { listId } });
  }, []);

  const renameList = useCallback((listId, name) => {
    dispatch({ type: ACTION.RENAME_LIST, payload: { listId, name } });
  }, []);

  const addItem = useCallback((listId, product) => {
    dispatch({
      type: ACTION.ADD_ITEM,
      payload: {
        listId,
        item: {
          itemCode: product.itemCode,
          description: product.description,
          productLine: product.productLine,
          subcategory: product.subcategory,
          imageUrl: product.imageUrl || null,
        },
      },
    });
  }, []);

  const removeItem = useCallback((listId, itemCode) => {
    dispatch({ type: ACTION.REMOVE_ITEM, payload: { listId, itemCode } });
  }, []);

  const shareList = useCallback((listId, user) => {
    dispatch({
      type: ACTION.SHARE_LIST,
      payload: { listId, sharedWithUser: { id: user.id, name: user.name, email: user.email } },
    });
  }, []);

  const markRead = useCallback((listId) => {
    dispatch({ type: ACTION.MARK_READ, payload: { listId } });
  }, []);

  return (
    <FavouritesContext.Provider value={{
      lists: state.lists,
      createList,
      deleteList,
      renameList,
      addItem,
      removeItem,
      shareList,
      markRead,
    }}>
      {children}
    </FavouritesContext.Provider>
  );
}

export function useFavourites() {
  const context = useContext(FavouritesContext);
  if (!context) throw new Error('useFavourites must be used within FavouritesProvider');
  return context;
}

export default FavouritesContext;
